/**
 * @file The home summary contract: one `users/{uid}/analyticsSummaries/{level}` document per scope
 * level the user may see, holding YTD booked, quoted per stage list, past-promised lines and overdue
 * invoices at that level. Written only by the analytics serving service's `/cycle` route, one
 * transaction per user, after every ingestion cycle; read by the owner's home page through a document
 * store. The rules decide readability from the document id, the granted level, and the owner's live
 * rights. `booked` and each `quotedByStage` value are the named queries' decimal cells verbatim, never
 * added across stage lists on the server. Every figure is its answer or its refusal, one refusal never
 * stops the others, and an absent figure predates its field.
 * `overdueInvoices` is the summary's own row of the one Org-level query the cycle runs, never another
 * scope's. `computedAt` is `Date#toISOString()`. A write whose `snapshotId` is lower than the stored
 * one is skipped, so a summary never moves back to an older build.
 */
import type { AnalyticsRefusalCode, AnalyticsScopeLevel, FactBuildProvenance, ResolvedAnalyticsPeriod } from './Analytics.NamedQuery.types';

export const ANALYTICS_CYCLE_ROUTE = '/cycle';

export const ANALYTICS_SUMMARIES_COLLECTION = 'analyticsSummaries';

export const analyticsSummaryPath = (uid: string, level: AnalyticsScopeLevel): string => `users/${uid}/${ANALYTICS_SUMMARIES_COLLECTION}/${level}`;

/** A summary figure its named query refused: `notConfigured` with `key` naming the binding or optional key, or `factTableOutdated` with `key` naming the fact table. */
export type AnalyticsSummaryRefusedFigure = { refusal: Extract<AnalyticsRefusalCode, 'notConfigured' | 'factTableOutdated'>; key: string };

export type AnalyticsSummary = {
  /** The granted level; equals the document id. */
  level: AnalyticsScopeLevel;
  /** YTD, resolved at the cycle call's time. */
  period: ResolvedAnalyticsPeriod;
  /** `salesClosedTotal`'s `ANALYTICS_BOOKED_COLUMN[level]` cell verbatim (sales credit at My sales, booked sales otherwise), or its refusal. */
  booked: string | AnalyticsSummaryRefusedFigure;
  /** `quoteValueTotal`'s `quotedAmount` cell for each stage list verbatim, or its refusal; absent on a summary written before the field existed. */
  quotedByStage?: { quote: string; buy: string } | AnalyticsSummaryRefusedFigure;
  /** `orderLinesTotals`'s `pastPromisedLines` cell, or its refusal; absent on a summary written before the field existed. */
  pastPromised?: number | AnalyticsSummaryRefusedFigure;
  /** The summary's own `overdueInvoices` cell of `overdueInvoicesBySummaryScope`, zero when it has no row, or its refusal; absent on a summary written before the field existed. */
  overdueInvoices?: number | AnalyticsSummaryRefusedFigure;
  computedAt: string;
  snapshotId: number;
  /** The builds of the answered figures, `booked`'s, then an answered `quotedByStage`'s, `pastPromised`'s and `overdueInvoices`'s, each fact table listed once. */
  builds: FactBuildProvenance[];
};
